/*
 * ******************************************************************
 * Copyright (c) 2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Injectable } from "@angular/core";
import { L10nService } from "@vmw/ngx-vip";
import { ColumnSpec } from "@shared/models/csv-export.model";

/**
 * Helper functions to convert data into CSV format and export as a file
 */
@Injectable()
export class CsvHelperService {
    public static readonly UNDERSCORE_SEPARATOR: string = '_';

    constructor(private l10nService: L10nService) {
    }

    public getData(rows: any[], columns: ColumnSpec[], includeAllColumns: boolean): string {
        let visibleColumns: ColumnSpec[];

        if (includeAllColumns) {
            visibleColumns = columns.filter(col => col !== null);
        } else {
            visibleColumns = columns.filter(col => col !== null)
                .filter(col => !col.hidden);
        }

        const columnCsvData =
            visibleColumns.map(col => this.l10nService.getMessage(col.labelResource))
                .map(msg => this.toCsvFormat(msg))
                .join(",");

        const rowsCsvData =
            rows.map(
                row => visibleColumns.map(col => this.getItemValueFromColumnField(row, col))
                    .map(value => this.toCsvFormat(value))
                    .join(",")
            ).join("\n");

        return columnCsvData + "\n" + rowsCsvData;
    }

    public toCsvFormat(itemValue: string): string {
        if (itemValue.indexOf('"') > -1) {
            itemValue = itemValue.replace(/"/g, '""');
        }
        itemValue = '"' + itemValue + '"';
        return itemValue;
    }

    /**
     * Extract value from nested JSON objects.
     */
    private getItemValueFromColumnField(item: any, column: ColumnSpec): string {
        const field: string = column.property;
        let value: string;

        if (field.indexOf(".") > 0) {
            const fieldNames = field.split(".");
            let itemValue: any = item;
            for (const str of fieldNames) {
                itemValue = itemValue[str];
                if (!itemValue) {
                    itemValue = item[field];
                    break;
                }
            }
            value = itemValue;
        } else {
            value = item[field];
        }

        // The value can be transformed and then localized if needed.
        if (column.transform) {
            value = column.transform(value);
        }

        if (column.localize && value !== null) {
            value = this.l10nService.getMessage(value);
        }

        // != is used intentionally - if value is numeric 0, we want to convert it to "0"
        return value != null ? value.toString() : "";
    }

    public generateCsvFilename(filename: string): string {
        const date: Date = new Date();

        return filename.concat(
            CsvHelperService.UNDERSCORE_SEPARATOR,
            date.toLocaleDateString(),
            CsvHelperService.UNDERSCORE_SEPARATOR,
            date.toLocaleTimeString(),
            ".csv");
    }

    count = 0;
    public downloadFile(content: string, filename: string): void {
        const csvFile: Blob = new Blob([content], {type: "text/csv"});

        const a: HTMLAnchorElement = document.createElement('a');
        a.href = window.URL.createObjectURL(csvFile);
        a.download = this.generateCsvFilename(filename);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
    }
}
